import type {CSSProperties, ChangeEvent, KeyboardEvent, FocusEvent} from 'react'
import omit from 'lodash/omit'
import {asHTMLAttributeValue, fromMaybe} from '@freckle/maybe'
import {replaceSmartQuotes as replaceSmartQuotesFn} from '@freckle/educator-entities/ts/common/helpers/string-helper'

type Props = {
  replaceSmartQuotes?: boolean
  onChange: (value: string) => void
  onEnter?: (value: string) => void
  onBlur?: (value: string) => void
  dataTest?: string
  type?: string
  className?: string
  autoFocus?: boolean
  value?: string | number | undefined
  name?: string
  title?: string
  pattern?: string
  placeholder?: string
  style?: CSSProperties
  id?: string | undefined
  disabled?: boolean
  autoCorrect?: string
  autoComplete?: string
  required?: boolean
  // ref isn't a real prop, and React doesn't know what to do
  // if the component that uses ref isn't an HTML element
  getRef?: (ref: HTMLElement) => void
}

export function Input(props: Props) {
  const {replaceSmartQuotes, onChange, onEnter, onBlur, dataTest} = props

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const rawValue = e.currentTarget.value
    const value = fromMaybe(() => false, replaceSmartQuotes)
      ? replaceSmartQuotesFn(rawValue)
      : rawValue
    onChange(value)
  }

  function handlePressEnter(e: KeyboardEvent) {
    if (e.currentTarget instanceof HTMLInputElement && e.key === 'Enter' && onEnter !== undefined) {
      const rawValue = e.currentTarget.value
      const value = fromMaybe(() => false, replaceSmartQuotes)
        ? replaceSmartQuotesFn(rawValue)
        : rawValue
      onEnter(value)
    }
  }

  function handleBlur(e: FocusEvent<HTMLInputElement>) {
    const rawValue = e.currentTarget.value
    const value = fromMaybe(() => false, replaceSmartQuotes)
      ? replaceSmartQuotesFn(rawValue)
      : rawValue
    if (onBlur) {
      onBlur(value)
    }
  }

  const inputProps = {
    ...omit(props, ['getRef', 'onChange', 'onEnter', 'replaceSmartQuotes', 'dataTest', 'onBlur']),
    onKeyDown: onEnter ? handlePressEnter : undefined,
    onChange: handleChange,
    onBlur: onBlur ? handleBlur : undefined,
    'data-test': asHTMLAttributeValue(dataTest),
  }
  return <input {...inputProps} />
}
